import { Box, CircularProgress } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";

import { AnimatedBackgroundPageWrapper } from "../layouts/AnimatedBackgroundPageWrapper";

interface MobileAuthLoadingProps {
  error?: string;
  onRetry?: () => void;
}

/**
 * Full-screen loading/error screen shown inside the native Capacitor app
 * while Cognito authentication is in progress after the user taps "Open"
 * from the email link.
 *
 * Styled to match the mobile redesign (warm background, bunny logo, MUI).
 */
export function MobileAuthLoading({ error, onRetry }: MobileAuthLoadingProps) {
  return (
    <AnimatedBackgroundPageWrapper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          padding: 3,
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
        }}
      >
        <img
          src={`${process.env.REACT_APP_BASE_PATH ?? "/"}brownAndRedBunny.png`}
          alt="Clipboard"
          style={{ height: 120, width: "auto" }}
        />

        {!error && (
          <>
            <CircularProgress size={36} sx={{ color: "text.primary" }} />
            <Title component="h6" variant="h6" sx={{ color: "text.primary" }}>
              Signing you in...
            </Title>
          </>
        )}

        {error && (
          <>
            <Title component="h6" variant="h6" sx={{ color: "error.main", textAlign: "center" }}>
              Sign in failed
            </Title>
            <Text variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
              {error}
            </Text>
            {onRetry && (
              <Text
                variant="body2"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: "text.primary",
                }}
                onClick={onRetry}
              >
                Back to login
              </Text>
            )}
          </>
        )}
      </Box>
    </AnimatedBackgroundPageWrapper>
  );
}
